import { cva } from 'class-variance-authority';

import type { IconName } from '../icon/iconMap';

export const variant = {
  primary: 'bg-primary text-primary-foreground',
  secondary: 'bg-secondary text-secondary-foreground',
  neutral: 'bg-neutral-hover text-neutral-foreground',
  muted: 'bg-neutral-muted-foreground text-primary-foreground',
  success: 'bg-success text-success-foreground',
  warning: 'bg-warning text-warning-foreground',
  destructive: 'bg-destructive text-destructive-foreground',
};

export type Variant = keyof typeof variant;
export type BorderVariant =
  | 'secondary'
  | 'success'
  | 'warning'
  | 'destructive'
  | 'neutral';
export type NonBorderVariant = Exclude<Variant, BorderVariant>;

export const size = {
  // Circular icon-only badge (formerly the standalone BadgeIcon component).
  icon: 'size-5 rounded-full p-0',
  sm: 'h-5 px-1.5 py-0.5 text-sm',
  md: 'h-6 px-2 py-0.5 text-base',
};

/**
 * Variants carrying a canonical default icon, so a `size="icon"` badge can
 * resolve its glyph from the variant alone. An explicit union (like
 * `BorderVariant`) so the props type can require an explicit `icon` for every
 * other variant; `Extract` keeps it honest if a variant is renamed or dropped.
 */
export type IconVariant = Extract<
  Variant,
  'success' | 'neutral' | 'warning' | 'destructive'
>;

/**
 * Default icon for an icon-size badge, keyed by variant. Used when a
 * `size="icon"` badge is rendered without an explicit `icon` prop. Total over
 * `IconVariant` — every other variant requires an explicit icon.
 */
export const badgeIconVariantMap: Record<IconVariant, IconName> = {
  success: 'check',
  neutral: 'minus',
  warning: 'alert-triangle',
  destructive: 'x',
};

/** Widened view of the map, for lookup by an unnarrowed `Variant`. */
const iconDefaults: Partial<Record<Variant, IconName>> = badgeIconVariantMap;

/**
 * The default icon for a variant, or `undefined` when it has none. The props
 * type makes the `undefined` case unreachable from a typed call site.
 */
export const getDefaultBadgeIcon = (variant: Variant): IconName | undefined =>
  iconDefaults[variant];

export const border = {
  false: 'border border-transparent',
  true: 'border border-solid',
};

export const badgeVariants = cva(
  'flex justify-center items-center min-w-0 font-normal rounded-sm gap-0.5',
  {
    variants: {
      variant,
      border,
      size,
    },
    defaultVariants: {
      variant: 'success',
      size: 'md',
    },
    compoundVariants: [
      {
        border: true,
        variant: 'destructive',
        class: 'bg-destructive-minimal border-destructive text-destructive',
      },
      {
        border: true,
        variant: 'warning',
        class: 'bg-warning-minimal border-warning text-warning',
      },
      {
        border: true,
        variant: 'success',
        class: 'bg-success-minimal border-success text-success',
      },
      {
        border: true,
        variant: 'secondary',
        class: 'bg-secondary-minimal border-secondary text-secondary',
      },
    ],
  }
);
